import React, {useEffect, useMemo, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Popover,
  SnackbarContent,
  Switch,
  Typography,
} from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import {CheckVerifiedDomainStatus} from "@evennt-tech/api-v2-external";
import {useSnackbar} from "notistack";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import Form, {BasicInput} from "view/components/Form";
import Alert from "view/components/Alert";
import {extractOrigin, isValidIframeUrl} from "tools/url/extractDomain";
import {useDomainStatus} from "hooks/verifiedDomain/useDomainStatus";
import {useMyVerifiedDomains} from "hooks/verifiedDomain/useMyVerifiedDomains";
import {useCreateVerifiedDomain} from "hooks/verifiedDomain/useVerifiedDomainMutations";
import RightArrowIcon from "view/images/RightArrowIcon";
import FlexBox from "view/components/Layout/FlexBox";
import {blue, grey} from "@material-ui/core/colors";
import {IframeData, IframeSizeMode} from "../../blots/IframeBlot";
import MyPendingDomainsModal from "./MyPendingDomainsModal";
import useStyles from "./styles";

type FormValues = {
  src: string;
  sizeMode: IframeSizeMode;
  widthPercent: number;
  widthPx: number | undefined;
  heightPx: number | undefined;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onInsert: (data: IframeData) => void;
  showId?: string;
  anchorEl?: HTMLElement | null;
  initial?: Partial<IframeData>;
};

const defaults: FormValues = {
  src: "",
  sizeMode: "percent",
  widthPercent: 100,
  widthPx: undefined,
  heightPx: undefined,
};

const VERIFICATION_SNACKBAR_MESSAGE =
  "Domain sent to FFAIR Team for verification.";
const INVALID_URL_MESSAGE = "Invalid URL";

const hasProtocol = (value: string): boolean =>
  /^[a-z][a-z\d+\-.]*:\/\//i.test(value);

const normalizeIframeUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed || hasProtocol(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const InsertIframeModal = ({
  open,
  onClose,
  onInsert,
  showId,
  anchorEl,
  initial,
}: Props) => {
  const classes = useStyles();
  const {enqueueSnackbar} = useSnackbar();
  const [showPendingList, setShowPendingList] = useState(false);
  const createDomain = useCreateVerifiedDomain();

  const form = useForm<FormValues>({
    mode: "all",
    defaultValues: {
      ...defaults,
      ...(initial && {
        src: initial.src ?? defaults.src,
        sizeMode: initial.sizeMode ?? defaults.sizeMode,
        widthPercent: initial.widthPercent ?? defaults.widthPercent,
        widthPx: initial.widthPx ?? defaults.widthPx,
        heightPx: initial.heightPx ?? defaults.heightPx,
      }),
    },
  });

  const url = form.watch("src");
  const normalizedUrl = useMemo(() => normalizeIframeUrl(url), [url]);
  const hasUrl = !!url.trim();
  const isUrlValid = useMemo(() => hasUrl && isValidIframeUrl(normalizedUrl), [
    hasUrl,
    normalizedUrl,
  ]);
  const isInvalidUrl = hasUrl && !isUrlValid;
  const sizeMode = form.watch("sizeMode");
  const widthPercent = form.watch("widthPercent");
  const widthPx = form.watch("widthPx");
  const heightPx = form.watch("heightPx");

  const {status, domain} = useDomainStatus(isUrlValid ? normalizedUrl : "");

  useEffect(() => {
    if (isInvalidUrl) {
      form.setError("src", {type: "validate", message: INVALID_URL_MESSAGE});
      return;
    }
    if (status === CheckVerifiedDomainStatus.REJECTED) {
      form.setError("src", {type: "manual", message: ""});
    } else {
      form.clearErrors("src");
    }
  }, [status, form, isInvalidUrl]);

  const [myDomains] = useMyVerifiedDomains(open);
  const pendingCount = useMemo(
    () => myDomains.filter((d) => d.isApproved === null).length,
    [myDomains]
  );

  const isCreating = createDomain.isLoading;
  const needsVerification =
    status === "unknown" || status === CheckVerifiedDomainStatus.PENDING;
  const hasSizeValues =
    sizeMode === "percent"
      ? widthPercent > 0 && widthPercent <= 100
      : (widthPx ?? 0) >= 1 && (heightPx ?? 0) >= 1;
  const submitDisabled =
    isInvalidUrl ||
    status === CheckVerifiedDomainStatus.REJECTED ||
    status === "loading" ||
    status === "idle" ||
    isCreating ||
    !hasSizeValues;

  const buttonLabel =
    status === "unknown" || status === CheckVerifiedDomainStatus.PENDING
      ? "Verify and insert"
      : "Insert";

  const handleInsertData = (values: FormValues): IframeData => ({
    src: normalizeIframeUrl(values.src),
    sizeMode: values.sizeMode,
    widthPercent:
      values.sizeMode === "percent" ? values.widthPercent : undefined,
    widthPx: values.sizeMode === "pixels" ? values.widthPx : undefined,
    heightPx: values.sizeMode === "pixels" ? values.heightPx : undefined,
  });

  const renderVerificationSnackbar = () => (
    <SnackbarContent
      classes={{
        root: classes.verificationSnackbar,
        message: classes.verificationSnackbarMessage,
      }}
      message={
        <FlexBox gap={2} alignCenter>
          <CheckCircleIcon
            htmlColor={blue.A100}
            className={classes.verificationSnackbarIcon}
          />
          <Typography className={classes.verificationSnackbarText}>
            {VERIFICATION_SNACKBAR_MESSAGE}
          </Typography>
        </FlexBox>
      }
    />
  );

  const onSubmit = async (values: FormValues) => {
    const data = handleInsertData(values);
    if (!isValidIframeUrl(data.src)) {
      form.setError("src", {type: "validate", message: INVALID_URL_MESSAGE});
      return;
    }
    if (status === "unknown") {
      const origin = extractOrigin(data.src);
      if (!origin) {
        form.setError("src", {
          type: "validate",
          message: INVALID_URL_MESSAGE,
        });
        return;
      }
      try {
        await createDomain.mutateAsync({url: origin, showId});
        enqueueSnackbar(VERIFICATION_SNACKBAR_MESSAGE, {
          variant: "success",
          content: renderVerificationSnackbar,
        });
      } catch (e) {
        enqueueSnackbar("Failed to send verification request", {
          variant: "error",
        });
        return;
      }
    }
    onInsert(data);
    setTimeout(() => onClose(), 0);
  };

  const renderStatusAlert = () => {
    if (isInvalidUrl) {
      return (
        <Alert severity="error" className={classes.alert}>
          {INVALID_URL_MESSAGE}
        </Alert>
      );
    }
    if (status === "loading") {
      return (
        <Typography variant="body2" className={classes.helper}>
          Checking domain...
        </Typography>
      );
    }
    if (status === CheckVerifiedDomainStatus.PENDING) {
      return (
        <Box className={classes.verificationAlert}>
          <ReportProblemOutlinedIcon className={classes.verificationIcon} />
          <Typography className={classes.verificationAlertText}>
            Domain needs verification by FFAIR Team
          </Typography>
        </Box>
      );
    }
    if (status === CheckVerifiedDomainStatus.REJECTED) {
      return (
        <Box className={classes.rejectedAlert}>
          <CancelIcon className={classes.rejectedIcon} />
          <Typography className={classes.rejectedText}>
            {"This Domain "}
            <strong>{domain}</strong>
            {" is Rejected"}
          </Typography>
        </Box>
      );
    }
    if (status === "unknown") {
      return (
        <Box className={classes.verificationAlert}>
          <ReportProblemOutlinedIcon className={classes.verificationIcon} />
          <Typography className={classes.verificationAlertText}>
            Domain needs verification by FFAIR Team
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const statusAlert = renderStatusAlert();

  const iframeForm = (
    <FfairPaper className={classes.paper}>
      <Form
        noDefaultStyles
        form={form}
        onSubmit={(e) => {
          e?.stopPropagation();
          return form.handleSubmit(onSubmit)(e);
        }}
      >
        <Typography className={classes.iframeLabel}>
          {initial?.src ? "Edit iframe" : "iFrame"}
        </Typography>
        {pendingCount > 0 && (
          <Box
            onClick={() => setShowPendingList(true)}
            className={classes.pendingRow}
          >
            <span>Waiting for Approval ({pendingCount})</span>
            <RightArrowIcon />
          </Box>
        )}
        <BasicInput
          className={needsVerification ? classes.verificationUrlField : ""}
          label="Insert your URL"
          name="src"
          rules={{
            required: true,
            validate: (value: string) =>
              isValidIframeUrl(normalizeIframeUrl(value)) ||
              INVALID_URL_MESSAGE,
          }}
          showHelperText={!!needsVerification}
          helperText={
            !!needsVerification && (
              <Typography
                component="span"
                className={classes.verificationHelper}
              >
                Since Domain is not verified yet it remains invisible to
                Exhibitors
              </Typography>
            )
          }
          InputProps={{
            endAdornment:
              status === "loading" ? (
                <InputAdornment position="end">
                  <CircularProgress
                    size={16}
                    className={classes.endAdornmentSpinner}
                  />
                </InputAdornment>
              ) : undefined,
          }}
        />
        <Box className={classes.sizeRow}>
          <Controller
            control={form.control}
            name="sizeMode"
            render={({field}) => (
              <Box className={classes.sizeToggle}>
                <Typography
                  className={classes.sizeToggleLabel}
                  style={{
                    color: field.value === "percent" ? "#424242" : "#9E9E9E",
                  }}
                >
                  in %
                </Typography>
                <Switch
                  checked={field.value === "pixels"}
                  onChange={(_, checked) =>
                    field.onChange(checked ? "pixels" : "percent")
                  }
                  color="primary"
                />
                <Typography
                  className={classes.sizeToggleLabel}
                  style={{
                    color: field.value === "pixels" ? "#424242" : "#9E9E9E",
                  }}
                >
                  in Pixels
                </Typography>
              </Box>
            )}
          />

          {sizeMode === "percent" ? (
            <Box className={classes.sizeInputWrap}>
              <BasicInput
                className={classes.sizeField}
                label="Size in %"
                name="widthPercent"
                type="number"
                rules={{
                  required: true,
                  min: 1,
                  max: 100,
                  valueAsNumber: true,
                }}
              />
            </Box>
          ) : (
            <Box className={classes.pixelInputs}>
              <BasicInput
                className={classes.sizeField}
                label="Width (px)"
                name="widthPx"
                type="number"
                rules={{required: true, min: 1, valueAsNumber: true}}
              />
              <BasicInput
                className={classes.sizeField}
                label="Height (px)"
                name="heightPx"
                type="number"
                rules={{required: true, min: 1, valueAsNumber: true}}
              />
            </Box>
          )}
        </Box>

        {statusAlert && (
          <Box className={needsVerification ? "" : classes.statusWrap}>
            {statusAlert}
          </Box>
        )}

        <Box className={classes.actions}>
          <Button
            className={classes.actionBtn}
            style={{color: "#2979FF"}}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className={classes.actionBtn}
            style={{color: submitDisabled ? "#9E9E9E" : "#2979FF"}}
            type="submit"
            disabled={submitDisabled}
          >
            {isCreating ? "Saving..." : buttonLabel}
          </Button>
        </Box>
      </Form>
    </FfairPaper>
  );

  return (
    <>
      {anchorEl ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={onClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            className: classes.popoverPaper,
            elevation: 0,
          }}
        >
          {iframeForm}
        </Popover>
      ) : (
        <FfairModal open={open} onClose={onClose}>
          {iframeForm}
        </FfairModal>
      )}
      <MyPendingDomainsModal
        open={showPendingList}
        onClose={() => setShowPendingList(false)}
      />
    </>
  );
};

export default InsertIframeModal;
